import { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SocialIcons from '../components/SocialIcons';
import { appConfig } from '../configs/app-config';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  message: '',
};

const initialErrors = {
  name: '',
  email: '',
  phone: '',
  message: '',
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateContactForm(values) {
  const errors = { ...initialErrors };

  const name = values.name.trim();
  if (!name) errors.name = 'Name is required';
  else if (name.length < 2) errors.name = 'Name must be at least 2 characters';

  const email = values.email.trim();
  if (!email) errors.email = 'Email is required';
  else if (!emailPattern.test(email)) errors.email = 'Enter a valid email address';

  const digitsOnly = values.phone.replace(/\D/g, '');
  if (!values.phone.trim()) errors.phone = 'Phone number is required';
  else if (digitsOnly.length < 10) errors.phone = 'Enter a valid 10 digit phone number (only number without country code)';

  const message = values.message.trim();
  if (!message) errors.message = 'Message is required';
  else if (message.length < 10)
    errors.message = 'Message must be at least 10 characters';

  return errors;
}

function hasValidationErrors(errors) {
  return Object.values(errors).some(Boolean);
}

export default function ContactUs() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState(initialErrors);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
    if (submitSuccess) setSubmitSuccess(false);
    if (submitError) setSubmitError('');
  };

  const handleBlur = (field) => () => {
    const nextErrors = validateContactForm(form);
    setErrors((prev) => ({ ...prev, [field]: nextErrors[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitSuccess(false);
    setSubmitError('');

    const nextErrors = validateContactForm(form);
    setErrors(nextErrors);
    if (hasValidationErrors(nextErrors)) return;

    if (!appConfig.web3formsAccessKey) {
      setSubmitError(
        'Unable to send your enquiry. Please try again. If the problem persists, please contact us directly.'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: appConfig.web3formsAccessKey,
          botcheck: '',
          subject: `New enquiry from ${form.name.trim()}`,
          from_name: form.name.trim(),
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          message: form.message.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error('Unable to send your enquiry. Please try again.');
      }

      setSubmitSuccess(true);
      setForm(initialForm);
    } catch (error) {
      setSubmitError(
        error.message || 'Something went wrong. Please try again or call us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>

      {/* ================= GOOGLE MAP ================= */}
      <Box sx={{ width: '100%', height: 350 }}>
        <iframe
          title="Google Map"
          src="https://www.google.com/maps?q=Neemuch%2C%20India&output=embed"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
        />
      </Box>

      {/* ================= MAIN SECTION ================= */}
      <Container sx={{ py: 7 }}>
        <Grid container spacing={5}>

          {/* -------- LEFT: CONTACT INFO -------- */}
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight={600} mb={2}>
              Get in Touch
            </Typography>

            <Typography color="text.secondary" mb={3}>
              We would love to hear from you. Reach out to us for product
              inquiries, bulk orders, or partnership opportunities.
            </Typography>

            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <LocationOnIcon color="primary" />
                <Typography>
                  Jai Ambe Herbs & Spices,<br />
                  Neemuch, Madhya Pradesh, India
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon fontSize="small" />
                <Typography variant="body2">
                {appConfig.appAuthorPhone}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon fontSize="small" />
                <Typography variant="body2">
                {appConfig.secodaryContactNumber}
                </Typography>
              </Stack>


              <Stack direction="row" spacing={2} alignItems="center">
                <EmailIcon color="primary" />
                <Typography>{appConfig.appAuthorEmail}</Typography>
              </Stack>
            </Stack>

            <Divider sx={{ my: 4 }} />

            {/* SOCIAL */}
            <Typography variant="h6" mb={1}>
              Connect With Us
            </Typography>

            <SocialIcons />

            <Divider sx={{ my: 4 }} />

            {/* BUSINESS HOURS */}
            <Typography variant="h6" mb={1}>
              Business Hours
            </Typography>

            <Typography color="text.secondary">
              Monday – Saturday: 9:00 AM – 7:00 PM<br />
              Sunday: Closed
            </Typography>
          </Grid>

          {/* -------- RIGHT: CONTACT FORM -------- */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 4,
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                borderRadius: 3,
                backgroundColor: '#fff',
              }}
            >
              <Typography variant="h5" fontWeight={600} mb={2}>
                Send Us a Message
              </Typography>

              {submitSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSubmitSuccess(false)}>
                  Thank you. Your enquiry has been sent. We will get back to you soon.
                </Alert>
              )}

              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
                  {submitError}
                </Alert>
              )}

              <Box component="form" noValidate onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    name="name"
                    label="Your Name"
                    fullWidth
                    required
                    value={form.name}
                    onChange={handleChange('name')}
                    onBlur={handleBlur('name')}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    inputProps={{ autoComplete: 'name' }}
                  />
                  <TextField
                    name="email"
                    type="email"
                    label="Email Address"
                    fullWidth
                    required
                    value={form.email}
                    onChange={handleChange('email')}
                    onBlur={handleBlur('email')}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    inputProps={{ autoComplete: 'email' }}
                  />
                  <TextField
                    name="phone"
                    type="tel"
                    label="Phone Number"
                    fullWidth
                    required
                    value={form.phone}
                    onChange={handleChange('phone')}
                    onBlur={handleBlur('phone')}
                    error={Boolean(errors.phone)}
                    helperText={errors.phone}
                    inputProps={{ autoComplete: 'tel' }}
                  />
                  <TextField
                    name="message"
                    label="Message"
                    multiline
                    rows={4}
                    fullWidth
                    required
                    value={form.message}
                    onChange={handleChange('message')}
                    onBlur={handleBlur('message')}
                    error={Boolean(errors.message)}
                    helperText={errors.message}
                    inputProps={{ 'aria-label': 'Your message' }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{ mt: 1 }}
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {isSubmitting ? 'Sending...' : 'Submit Enquiry'}
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Grid>

        </Grid>
      </Container>

      {/* ================= EXTRA CONTENT ================= */}
      <Box sx={{ backgroundColor: '#FAF7F2', py: 6 }}>
        <Container>
          <Typography variant="h4" fontWeight={600} mb={2}>
            Why Contact Jai Ambe Herbs & Spices?
          </Typography>

          <Typography color="text.secondary" maxWidth={800}>
            We specialize in supplying premium quality herbs, spices, and oils
            for retailers, wholesalers, and food businesses. Our team ensures
            prompt communication, competitive pricing, and consistent quality.
          </Typography>
        </Container>
      </Box>

      {/* ================= FINAL CTA ================= */}
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={600}>
          Looking for Bulk Orders or Custom Packaging?
        </Typography>

        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ mt: 2 }}
          href={`tel:${appConfig.appAuthorPhone}`}
        >
          Call Us Now
        </Button>
      </Box>

    </Box>
  );
}
